/**
 * BuildActivityPanel — "what the build is doing / what got built" surface.
 *
 * One box, shown under the user prompt. Structure mirrors the room's worker
 * stream box (AgentResponsePanel): a bordered, resizable, scrollable reader
 * showing the SELECTED agent's generated prompt with a usage chip, and a
 * footer LIST of the network's agents (the "Network built · N/N" roster) —
 * click a row to load that agent's prompt into the reader above.
 *
 * Driven by the reducer's ``buildActivity`` slice. LIVE (``live`` prop, during
 * ``isProcessing``): rows flip queued → writing → done and skills stream in,
 * with a spinner + active phase label. PERSISTED (revisit): the slice is
 * rehydrated from the AGENT rows (HYDRATE_BUILD) so prompts + per-agent usage
 * survive a reload — matching the room's persisted feed.
 */
import React, { useState, useCallback, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Check, Loader2, Circle, Sparkles, AlertTriangle, GripHorizontal, PauseCircle, Brain, ChevronDown, ChevronRight } from 'lucide-react';
import { useResizable } from '@stepflow/ui-shared/hooks/useResizable';
import { MODEL_ID_TO_FAMILY, MODEL_ROWS } from '@stepflow/ui-shared/services/generatedPricing';

const PHASE_LABEL = {
  DESIGNING: 'Designing the network',
  WRITING_PROMPTS: 'Writing agent roles',
  DRAFTING_SKILLS: 'Drafting reusable skills',
  FINALIZING: 'Finalizing',
};

// ─── Usage chip — same logic/shape as the room's AgentResponsePanel ──────────
function _fmtTokens(n) {
  const v = Number(n) || 0;
  if (v >= 1_000_000) return `${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `${(v / 1_000).toFixed(1)}k`;
  return String(v);
}
function _modelLabel(modelId) {
  if (!modelId) return '';
  const short = String(modelId)
    .replace(/^global\.anthropic\./, '')
    .replace(/^anthropic\./, '')
    .replace(/^us\.anthropic\./, '')
    .replace(/^eu\.anthropic\./, '')
    .replace(/-\d{8}-v\d.*$/, '')
    .replace(/-/g, ' ');
  if (short) return short;
  const fam = MODEL_ID_TO_FAMILY[modelId];
  return fam ? (MODEL_ROWS[fam]?.label ?? fam) : modelId;
}

/** Short model tag for a per-lane chip: "sonnet 4 5" → "Sonnet", opus → "Opus". */
function _modelTag(modelId) {
  const s = String(modelId || '').toLowerCase();
  if (s.includes('opus')) return 'Opus';
  if (s.includes('sonnet')) return 'Sonnet';
  if (s.includes('haiku')) return 'Haiku';
  const lbl = _modelLabel(modelId);
  return lbl ? lbl.split(' ').slice(0, 2).join(' ') : '';
}

/** Compact right-aligned per-lane usage chip: ``Model · $ · pts``. Renders the
 *  status word (ready/queued/…) when there's no usage. */
function LaneUsage({ usage, fallback }) {
  if (!usage || typeof usage !== 'object') {
    return <span className="ml-auto shrink-0 text-[10px] text-gray-400">{fallback}</span>;
  }
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  const tag = _modelTag(usage.modelId);
  return (
    <span className="ml-auto flex shrink-0 items-center gap-1 text-[10px] tabular-nums text-slate-400 font-mono">
      {tag && <span className="text-slate-500">{tag}</span>}
      {cost > 0 && <span>{cost >= 0.01 ? `$${cost.toFixed(2)}` : `$${cost.toFixed(4)}`}</span>}
      {points > 0 && <span className="text-slate-500">{points.toLocaleString()} pts</span>}
    </span>
  );
}

function UsageBar({ usage }) {
  if (!usage || typeof usage !== 'object') return null;
  const inT = Number(usage.inputTokens) || 0;
  const outT = Number(usage.outputTokens) || 0;
  const cacheRead = Number(usage.cacheReadTokens) || 0;
  const cacheCreation = Number(usage.cacheCreationTokens) || 0;
  const totalIn = inT + cacheRead + cacheCreation;
  const cost = Number(usage.estimatedCostUsd) || 0;
  const points = Number(usage.points) || 0;
  if (totalIn === 0 && outT === 0 && points === 0) return null;
  const label = _modelLabel(usage.modelId);
  const breakdown = cacheRead > 0 ? ` (${_fmtTokens(cacheRead)} cached)` : '';

  const parts = [];
  if (label) parts.push(label);
  if (cost > 0) parts.push(`$${cost.toFixed(4)}`);
  parts.push(`↓${_fmtTokens(totalIn)}${breakdown} ↑${_fmtTokens(outT)}`);
  if (usage.calls > 1) parts.push(`${usage.calls} calls`);
  if (points > 0) parts.push(`${points.toLocaleString()} pts`);

  return (
    <div className="border-t border-slate-200 bg-slate-50/80 px-3 py-1 text-[10px] tabular-nums text-slate-500 font-mono flex items-center gap-1 whitespace-nowrap overflow-x-auto">
      {parts.map((p, i) => (
        <React.Fragment key={i}>
          {i > 0 && <span className="text-slate-300">|</span>}
          <span>{p}</span>
        </React.Fragment>
      ))}
    </div>
  );
}

// ─── Footer roster rows (the list) ───────────────────────────────────────────
function AgentListRow({ row, selected, onSelect }) {
  const phase = row.phase || 'queued';
  const clickable = typeof row.prompt === 'string' && row.prompt.length > 0;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? () => onSelect(row.agentId) : undefined}
      className={`flex items-center gap-2 rounded px-1.5 py-1 ${clickable ? 'cursor-pointer hover:bg-gray-100' : ''} ${selected ? 'bg-gray-100' : ''}`}
      title={clickable ? 'View this agent’s prompt' : undefined}
    >
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {phase === 'done' && !row.error ? (
          <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden />
        ) : phase === 'done' && row.error ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden />
        ) : phase === 'writing' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" aria-hidden />
        ) : (
          <Circle className="h-2 w-2 text-gray-300" aria-hidden />
        )}
      </span>
      <span className={`truncate text-xs ${selected ? 'font-medium text-gray-900' : phase === 'done' ? 'text-gray-700' : phase === 'writing' ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>
        {row.label || row.agentId}
      </span>
      <LaneUsage
        usage={phase === 'done' && !row.error ? row.usage : null}
        fallback={phase === 'writing' ? 'writing…' : phase === 'done' ? (row.error ? 'skipped' : 'ready') : 'queued'}
      />
    </motion.div>
  );
}

function SkillRow({ row, selected, onSelect }) {
  const phase = row.phase || 'queued';
  const done = phase === 'done';
  const clickable = typeof row.instructions === 'string' && row.instructions.length > 0;
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      onClick={clickable ? () => onSelect(row.name) : undefined}
      className={`flex items-center gap-2 rounded px-1.5 py-1 ${clickable ? 'cursor-pointer hover:bg-gray-100' : ''} ${selected ? 'bg-gray-100' : ''}`}
      title={clickable ? 'View this skill' : (row.whenToUse || undefined)}
    >
      <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
        {done && !row.error ? (
          <Check className="h-3.5 w-3.5 text-violet-500" aria-hidden />
        ) : done && row.error ? (
          <AlertTriangle className="h-3.5 w-3.5 text-amber-500" aria-hidden />
        ) : phase === 'drafting' ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin text-violet-400" aria-hidden />
        ) : (
          <Circle className="h-2 w-2 text-gray-300" aria-hidden />
        )}
      </span>
      <span className={`truncate text-xs ${selected ? 'font-medium text-gray-900' : done ? 'text-gray-700' : phase === 'drafting' ? 'text-gray-900 font-medium' : 'text-gray-400'}`}>{row.name}</span>
      {/* Each skill now has its OWN body-draft call → its own cost chip (parallel
          SkillFactory). Falls back to status word while queued/drafting. */}
      <LaneUsage
        usage={done && !row.error ? row.usage : null}
        fallback={phase === 'drafting' ? 'drafting…' : done ? (row.error ? 'skipped' : 'added') : 'queued'}
      />
    </motion.div>
  );
}

/**
 * ReasoningBlock — the architect's live/persisted thought stream. While ``streaming``
 * it auto-expands and pins the scroll to the newest text (so the user watches it think);
 * once settled it's collapsible and defaults collapsed on revisit.
 */
function ReasoningBlock({ text, streaming }) {
  const [open, setOpen] = useState(streaming);
  const bodyRef = React.useRef(null);
  // Follow the stream: keep the newest text in view while it's still arriving.
  useEffect(() => {
    if (streaming && open && bodyRef.current) {
      bodyRef.current.scrollTop = bodyRef.current.scrollHeight;
    }
  }, [text, streaming, open]);
  // Auto-expand when streaming starts.
  useEffect(() => { if (streaming) setOpen(true); }, [streaming]);

  return (
    <div className="border-b border-slate-200 bg-white/40">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="flex w-full items-center gap-1.5 px-3 py-1.5 text-left"
        data-testid="build-reasoning-toggle"
      >
        {open ? <ChevronDown className="h-3 w-3 text-slate-400" /> : <ChevronRight className="h-3 w-3 text-slate-400" />}
        <Brain className={`h-3.5 w-3.5 ${streaming ? 'text-blue-500' : 'text-slate-400'}`} aria-hidden />
        <span className="text-[10px] font-medium uppercase tracking-wide text-slate-400">
          Architect reasoning
        </span>
        {streaming && <Loader2 className="h-3 w-3 animate-spin text-blue-500" aria-hidden />}
      </button>
      {open ? (
        <div
          ref={bodyRef}
          className="max-h-48 overflow-y-auto whitespace-pre-wrap px-3 pb-2 font-mono text-[11px] leading-relaxed text-slate-600 [scrollbar-gutter:stable]"
          data-testid="build-reasoning-body"
        >
          {text}
        </div>
      ) : null}
    </div>
  );
}

export default function BuildActivityPanel({
  buildActivity, live = true, awaitingApproval = false, prompt = null,
  networkId = null, builds = null,
}) {
  // ── Revision switcher ──────────────────────────────────────────────────
  // ``builds`` (newest first, from GET /networks/{id}) lists every revision.
  // Selecting an older one fetches its immutable manifest and overrides the
  // live/latest slice. A live build always shows the current slice (no override).
  const revisions = Array.isArray(builds) ? builds : [];
  const [revOverride, setRevOverride] = useState(null); // {taskId, agents{}, skills{}, prompt}
  const [revLoading, setRevLoading] = useState(false);

  const latestTaskId = buildActivity?.taskId || (revisions[0] && revisions[0].taskId) || null;
  const activeTaskId = revOverride?.taskId || latestTaskId;

  const selectRevision = useCallback(async (taskId) => {
    if (!networkId || !taskId) return;
    if (taskId === latestTaskId) { setRevOverride(null); return; }
    setRevLoading(true);
    try {
      const { getBuildManifest } = await import('@stepflow/ui-shared/services/networkExecutionsApi');
      const m = await getBuildManifest(networkId, taskId);
      if (!m) { setRevOverride(null); return; }
      const aRows = {};
      for (const a of (Array.isArray(m.agents) ? m.agents : [])) {
        if (!a || !a.logicalId || !a.systemPrompt) continue;
        aRows[a.logicalId] = { agentId: a.logicalId, label: a.logicalId, phase: 'done', prompt: a.systemPrompt, ...(a.usage ? { usage: a.usage } : {}) };
      }
      const sRows = {};
      for (const s of (Array.isArray(m.skills) ? m.skills : [])) {
        if (!s || !s.name) continue;
        sRows[s.name] = { name: s.name, phase: 'done', ...(s.skillId ? { skillId: s.skillId } : {}), ...(s.whenToUse ? { whenToUse: s.whenToUse } : {}), ...(s.instructions ? { instructions: s.instructions } : {}), ...(s.usage ? { usage: s.usage } : {}) };
      }
      setRevOverride({
        taskId, agents: aRows, skills: sRows,
        prompt: typeof m.prompt === 'string' ? m.prompt : null,
        architectUsage: (m.architectUsage && typeof m.architectUsage === 'object') ? m.architectUsage : null,
        // The versioned reasoning for THIS revision (from builds/{taskId}.json) — so the
        // reasoning block follows the selected revision, not just the live stream.
        reasoning: typeof m.architectReasoning === 'string' ? m.architectReasoning : '',
        reasoningDone: true,
      });
    } finally {
      setRevLoading(false);
    }
  }, [networkId, latestTaskId]);

  // The slice actually rendered: an older revision's manifest, else the live/latest.
  const ba = revOverride || buildActivity || {};
  // STABLE ORDER: sort by a fixed key (agentId / skill name) so rows never visually
  // jump/reshuffle as concurrent BUILD_*_ROW events (DistributedMap fires many at once)
  // mutate the dict. Deterministic + independent of arrival order.
  const _byId = (a, b) => String(a.agentId || a.name || '').localeCompare(String(b.agentId || b.name || ''));
  const agents = ba.agents ? Object.values(ba.agents).sort(_byId) : [];
  const skills = ba.skills ? Object.values(ba.skills).sort(_byId) : [];
  const architectUsage = ba.architectUsage || null;
  // Skills cost is ONE draft call — aggregate across rows into a group total
  // (model from the row that carries usage; sum cost + points defensively).
  const skillsUsage = (() => {
    const withUsage = skills.filter((s) => s.usage && typeof s.usage === 'object');
    if (withUsage.length === 0) return null;
    let cost = 0; let points = 0; let modelId = null;
    for (const s of withUsage) {
      cost += Number(s.usage.estimatedCostUsd) || 0;
      points += Number(s.usage.points) || 0;
      if (!modelId) modelId = s.usage.modelId;
    }
    return { modelId, estimatedCostUsd: cost, points };
  })();
  // Prompt: the override's stored prompt, else the live chat message.
  const promptTextRaw = revOverride
    ? revOverride.prompt
    : (ba.prompt || (prompt && prompt.content) || null);
  // Display-only whitespace tidy: ``whitespace-pre-wrap`` renders every newline
  // verbatim, so a prompt that arrived with 3-4 consecutive newlines shows a
  // huge gap between paragraphs. Collapse any run of blank lines to a SINGLE
  // blank line (2 newlines) + trim trailing space per line. Cosmetic only —
  // does NOT change the stored prompt or what was sent to the Brain.
  const promptText = typeof promptTextRaw === 'string'
    ? promptTextRaw.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n')
    : promptTextRaw;
  const promptTime = revOverride ? null : (prompt && prompt.timestamp) || null;

  // Reader box height — same draggable mechanic as the room's worker box.
  const { height, onMouseDown } = useResizable({
    minHeight: 60, defaultHeight: 150,
    maxHeight: typeof window !== 'undefined' ? window.innerHeight * 0.6 : 400,
  });

  // What's loaded in the reader: an agent prompt OR a skill body. ``{kind,id}``.
  const [sel, setSel] = useState(null);
  const selectAgent = useCallback((id) => setSel({ kind: 'agent', id }), []);
  const selectSkill = useCallback((name) => setSel({ kind: 'skill', id: name }), []);
  // Auto-select: the agent being written live, else the first agent w/ a prompt.
  useEffect(() => {
    const writing = agents.find((a) => a.phase === 'writing' && a.prompt);
    const firstAgent = agents.find((a) => a.prompt);
    setSel((cur) => {
      if (cur && cur.kind === 'agent' && agents.some((a) => a.agentId === cur.id && a.prompt)) return cur;
      if (cur && cur.kind === 'skill' && skills.some((s) => s.name === cur.id && s.instructions)) return cur;
      if (writing) return { kind: 'agent', id: writing.agentId };
      if (firstAgent) return { kind: 'agent', id: firstAgent.agentId };
      return null;
    });
  }, [agents, skills, activeTaskId]);

  // Stay mounted whenever there's ANYTHING to show — a prompt, a phase, rows, or
  // we're live/awaiting. The caller (ChatWorkspace) owns the prompt here as the
  // single window, so collapsing on "empty" would drop the prompt back to a bare
  // bubble. Only return null when truly nothing exists to render.
  if (!live && !awaitingApproval && !promptText && !ba.phase
      && agents.length === 0 && skills.length === 0
      && !(typeof ba.reasoning === 'string' && ba.reasoning.length > 0)) return null;

  const doneCount = agents.filter((a) => a.phase === 'done').length;
  // Awaiting approval = paused mid-build (NOT done): show a paused label, not
  // "Network built" and not an active phase spinner.
  const phaseLabel = awaitingApproval
    ? 'Awaiting approval'
    : (live ? (PHASE_LABEL[ba.phase] || 'Building your network') : 'Network built');

  // Resolve the reader's content (prompt text + usage + a label).
  let readerBody = null;
  let readerUsage = null;
  let readerLabel = null;
  if (sel && sel.kind === 'agent') {
    const a = agents.find((x) => x.agentId === sel.id);
    if (a && a.prompt) { readerBody = a.prompt; readerUsage = a.usage; readerLabel = a.label || a.agentId; }
  } else if (sel && sel.kind === 'skill') {
    const s = skills.find((x) => x.name === sel.id);
    if (s && s.instructions) {
      readerBody = (s.whenToUse ? `When to use: ${s.whenToUse}\n\n` : '') + s.instructions;
      readerUsage = s.usage; readerLabel = s.name;
    }
  }

  // Revision index for the header label (1-based, oldest=1).
  const revCount = revisions.length;
  const activeRevIdx = activeTaskId ? revisions.findIndex((r) => r.taskId === activeTaskId) : -1;
  const revNumber = activeRevIdx >= 0 ? revCount - activeRevIdx : revCount;

  return (
    <motion.div
      layout
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-w-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-50/80 shadow-sm"
      data-testid="build-activity-panel"
    >
      {/* Header — phase / status, selected item, revision switcher. */}
      <div className="flex items-center gap-2 border-b border-slate-200 bg-white/60 px-3 py-1.5">
        {awaitingApproval
          ? <PauseCircle className="h-3.5 w-3.5 text-amber-500" aria-hidden />
          : live
            ? <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500" aria-hidden />
            : <Check className="h-3.5 w-3.5 text-emerald-500" aria-hidden />}
        <span className="text-[11px] font-semibold text-slate-800">{phaseLabel}</span>
        {!live && revCount > 1 && (
          <span className="text-[10px] text-slate-500">· rev {revNumber} of {revCount}</span>
        )}
        {readerLabel && (
          <span className="truncate text-[10px] text-slate-400">· {readerLabel}</span>
        )}
        {/* Revision switcher — only when there's history and the build is settled. */}
        {!live && revCount > 1 && (
          <select
            value={activeTaskId || ''}
            onChange={(e) => selectRevision(e.target.value)}
            disabled={revLoading}
            className="ml-auto rounded border border-slate-200 bg-white px-1.5 py-0.5 text-[10px] text-slate-600"
            data-testid="build-revision-select"
            title="View a previous build revision"
          >
            {revisions.map((r, i) => (
              <option key={r.taskId} value={r.taskId}>
                rev {revCount - i}{i === 0 ? ' (latest)' : ''}
              </option>
            ))}
          </select>
        )}
        {agents.length > 0 && (
          <span className={`${(!live && revCount > 1) ? '' : 'ml-auto'} shrink-0 text-[10px] text-slate-400`}>
            {doneCount}/{agents.length} agents
          </span>
        )}
      </div>

      {/* Prompt — the request that kicked off this build, as the box's top
          section (room-worker style), so the whole build lives in one box. */}
      {promptText ? (
        <div className="border-b border-slate-200 bg-white/40 px-3 py-2">
          <div className="mb-1 text-[10px] font-medium uppercase tracking-wide text-slate-400">Prompt</div>
          <div className="max-h-40 overflow-y-auto whitespace-pre-wrap text-[12px] leading-normal text-slate-700 [scrollbar-gutter:stable]">
            {promptText}
          </div>
          {promptTime ? (
            <div className="mt-1.5 text-[10px] text-slate-400">{new Date(promptTime).toLocaleTimeString()}</div>
          ) : null}
        </div>
      ) : null}

      {/* Architect reasoning — streams live (keeps the user engaged while the
          architect thinks) and stays readable afterward (persisted BUILD_REASONING
          'done'). Auto-expanded while streaming; collapsible once settled. */}
      {(ba.reasoning && ba.reasoning.length > 0) ? (
        <ReasoningBlock text={ba.reasoning} streaming={live && !ba.reasoningDone} />
      ) : null}

      {/* Reader — the selected agent's prompt OR skill body (scroll + resize). */}
      {readerBody ? (
        <>
          <div
            style={{ height: `${height}px` }}
            className="min-w-0 overflow-y-auto overflow-x-hidden px-3 py-2 text-[12px] leading-relaxed text-slate-800 [scrollbar-gutter:stable] whitespace-pre-wrap"
          >
            {readerBody}
          </div>
          <UsageBar usage={readerUsage} />
          <div
            onMouseDown={onMouseDown}
            className="flex items-center justify-center h-2 cursor-row-resize border-t border-slate-200/80 bg-slate-50/90 hover:bg-slate-100 transition-colors group"
          >
            <GripHorizontal className="h-2.5 w-3.5 text-slate-400 group-hover:text-slate-600" />
          </div>
        </>
      ) : null}

      {/* Footer — architect lane, agent roster, then the Skills deck. Each lane
          carries its OWN usage chip (model · $ · pts). */}
      <div className="border-t border-slate-200 bg-slate-50/60 px-2 py-1.5">
        {/* Architect (design step, opus/reason tier) — its own lane + usage. */}
        {architectUsage && (
          <div className="mb-1.5 flex items-center gap-2 rounded px-1.5 py-1">
            <span className="flex h-3.5 w-3.5 shrink-0 items-center justify-center">
              <Sparkles className="h-3.5 w-3.5 text-amber-500" aria-hidden />
            </span>
            <span className="truncate text-xs text-gray-700">Architect</span>
            <LaneUsage usage={architectUsage} fallback="" />
          </div>
        )}

        {agents.length > 0 && (
          <div className="space-y-0.5">
            <div className="mb-1 flex items-center gap-1.5">
              <span className="text-[11px] font-medium text-slate-600">Agents</span>
              <span className="text-[10px] text-slate-400">{agents.length}</span>
            </div>
            <AnimatePresence initial={false}>
              {agents.map((row) => (
                <AgentListRow
                  key={row.agentId}
                  row={row}
                  selected={sel && sel.kind === 'agent' && sel.id === row.agentId}
                  onSelect={selectAgent}
                />
              ))}
            </AnimatePresence>
          </div>
        )}

        {skills.length > 0 && (
          <div className="mt-2 border-t border-slate-200 pt-2">
            <div className="mb-1 flex items-center gap-1.5">
              <Sparkles className="h-3 w-3 text-violet-500" aria-hidden />
              <span className="text-[11px] font-medium text-slate-600">Skills</span>
              <span className="text-[10px] text-slate-400">{skills.length}</span>
              {/* Skills are drafted in ONE LLM call, so cost is a GROUP total on
                  the header — not a per-skill chip (which would falsely imply
                  one skill cost everything and the rest were free). */}
              <LaneUsage usage={skillsUsage} fallback="" />
            </div>
            <div className="space-y-0.5">
              <AnimatePresence initial={false}>
                {skills.map((row) => (
                  <SkillRow
                    key={row.name}
                    row={row}
                    selected={sel && sel.kind === 'skill' && sel.id === row.name}
                    onSelect={selectSkill}
                  />
                ))}
              </AnimatePresence>
            </div>
          </div>
        )}
      </div>
    </motion.div>
  );
}
